import React, { useState, useEffect, useCallback, useRef, useContext } from "react";
import {
  Plus,
  FileText,
  Book,
  Edit,
  ExternalLink,
  Building,
  ChevronUp,
  ArrowRight,
  ArrowLeft,
  GripVertical
} from "lucide-react";
import { DragDropContext as RBDDragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import API from "../../../../config/axiosConfig.js";
import { UserContext } from "../../../../context/UserContext.jsx";
import {
  useTrainingTopics,
  useTrainingTests
} from "../../hooks/index.js";
import {
  CustomSelect,
  QuestionForm,
  TestCard,
  FloatingButton
} from "../../components/index.js";
import StrictModeDroppable from "../../components/StrictModeDroppable.jsx"
import {
  useImageHandling,
  getClientViewUrl
} from "../../utils/imageHelpers.js";
import TrainingCreation from "../TrainingCreation/TrainingCreation.jsx";
import "./Training.css";

// A wrapper component to handle the defaultProps warning
const DragDropContext = (props) => {
  return <RBDDragDropContext {...props} />;
};

// Main Training Component
const Training = () => {
  const { user, hospitals: allHospitals, permittedApps } = useContext(UserContext);
  const [hospitals, setHospitals] = useState([]);
  const [hospitalsLoading, setHospitalsLoading] = useState(true);

  // States for component mode
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [isViewingTest, setIsViewingTest] = useState(false);
  const [editingTestId, setEditingTestId] = useState(null);
  const [showNameEntryScreen, setShowNameEntryScreen] = useState(false);
  const [newTestName, setNewTestName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  
  // States for data selections
  const [selectedHospitalId, setSelectedHospitalId] = useState("");
  const [selectedTopicId, setSelectedTopicId] = useState("");
  const [selectedTopicName, setSelectedTopicName] = useState("");
  const [selectedTestId, setSelectedTestId] = useState("");
  
  // State for topic renaming
  const [isRenamingTopic, setIsRenamingTopic] = useState(false);
  const [newTopicNameInput, setNewTopicNameInput] = useState("");
  const [topicToRename, setTopicToRename] = useState(null);
  
  // State for drag and drop
  const [isDragMode, setIsDragMode] = useState(false);
  const [isSavingOrder, setIsSavingOrder] = useState(false);
  
  const stableIdMapRef = useRef({});
  
  const {
    previews: questionImagePreviews,
    updateImage: updateQuestionImage,
    removeImage: removeQuestionImage,
    setPreviewUrl: setQuestionPreviewUrl,
    clearAllImages: clearAllQuestionImages
  } = useImageHandling();

  const {
    previews: explanationImagePreviews,
    updateImage: updateExplanationImage,
    removeImage: removeExplanationImage,
    setPreviewUrl: setExplanationPreviewUrl,
    clearAllImages: clearAllExplanationImages
  } = useImageHandling();

  useEffect(() => {
    if (allHospitals && allHospitals.length > 0 && user && permittedApps) {
      const trainingApp = permittedApps.find(app => app.key === 'TRAINING');
      let editableHospitals = [];

      if (user.isSuperAdmin) {
        editableHospitals = [...allHospitals];
      } else if (trainingApp) {
        const permittedHospitalIds = trainingApp.admins
          .filter(admin => admin.user._id === user._id)
          .map(admin => admin.hospital._id);
        
        editableHospitals = allHospitals.filter(hospital =>
          permittedHospitalIds.includes(hospital._id)
        );
      }
      
      setHospitals(editableHospitals);
      setHospitalsLoading(false);
    }
  }, [allHospitals, user, permittedApps]);
  
  const {
    topics,
    loading: topicsLoading,
    refreshTopics
  } = useTrainingTopics(selectedHospitalId);
  
  const {
    tests,
    loading: testsLoading,
    error: testsError,
    topicData,
    setTests,
    refreshTests 
  } = useTrainingTests(selectedTopicId, selectedHospitalId);

  const refreshTestsFromHookRef = useRef(null);
  useEffect(() => {
    refreshTestsFromHookRef.current = refreshTests;
  }, [refreshTests]);


  const safeTests = Array.isArray(tests) ? tests : [];
  const [showTests, setShowTests] = useState(false);

  useEffect(() => {
    if (safeTests.length > 0 && isDragMode) {
      safeTests.forEach((test) => {
        const testId = test.id || test._id;
        if (testId && !stableIdMapRef.current[testId]) {
          stableIdMapRef.current[testId] = `test-${testId}`;
        }
      });
    }
  }, [safeTests, isDragMode]);

  const getStableId = useCallback((testId) => {
    if (!testId) return `test-fallback-${Math.random().toString(36).substr(2, 9)}`;
    if (!stableIdMapRef.current[testId]) {
      stableIdMapRef.current[testId] = `test-${testId}`;
    }
    return stableIdMapRef.current[testId];
  }, []);

  const clearAllImageStates = () => {
    clearAllQuestionImages();
    clearAllExplanationImages();
  };

  const refreshTestsData = useCallback(() => {
    if (refreshTestsFromHookRef.current && typeof refreshTestsFromHookRef.current === 'function' && selectedHospitalId && selectedTopicId) {
      refreshTestsFromHookRef.current();
    }
  }, [selectedHospitalId, selectedTopicId]); 

  useEffect(() => {
    const shouldRefresh = selectedHospitalId && selectedTopicId && showTests && !isViewingTest && !isCreatingNew;
    if (shouldRefresh) {
      refreshTestsData();
    }
  }, [selectedHospitalId, selectedTopicId, showTests, isCreatingNew, refreshTestsData]);


  useEffect(() => {
    if (!hospitalsLoading && hospitals.length === 1 && !selectedHospitalId) {
      setSelectedHospitalId(hospitals[0]._id);
    }
  }, [hospitals, hospitalsLoading, selectedHospitalId]);

  useEffect(() => {
    if (topicData?.hospital?.id && !selectedHospitalId) {
      setSelectedHospitalId(topicData.hospital.id);
    }
  }, [topicData, selectedHospitalId]);

  const handleDragEnd = useCallback(async (result) => {
    if (!result.destination || result.destination.index === result.source.index) return;
    
    const items = Array.from(safeTests);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    
    const updatedTests = items.map((test, index) => ({ ...test, order: index }));
    setTests(updatedTests); 
    
    const orderUpdateData = updatedTests.map(test => ({ id: test.id || test._id, order: test.order })).filter(item => item.id);
    
    setIsSavingOrder(true);
    try {
      await API.put(
        `/api/trainings/training-topic/${encodeURIComponent(selectedTopicId)}/hospital/${encodeURIComponent(selectedHospitalId)}/update-order`,
        orderUpdateData
      );
    } catch (error) {
      console.error("Error updating test order:", error);
      alert(`שגיאה בעדכון סדר המבחנים: ${error.response?.data?.message || error.message}`);
      refreshTestsData(); 
    } finally {
      setIsSavingOrder(false);
    }
  }, [safeTests, selectedTopicId, selectedHospitalId, setTests, refreshTestsData]);

  const toggleDragMode = useCallback(() => {
    setIsDragMode(prev => {
      if (prev) stableIdMapRef.current = {};
      return !prev;
    });
  }, []);

  const handleShowTests = () => {
    setShowTests(true);
  };
  
  const handleQuestionImageChange = (questionIndex, e) => {
    if (e.target.files && e.target.files.length > 0) updateQuestionImage(questionIndex, e.target.files[0]);
  };

  const handleExplanationImageChange = (questionIndex, e) => {
    if (e.target.files && e.target.files.length > 0) updateExplanationImage(questionIndex, e.target.files[0]);
  };

  const handleRemoveQuestionImage = (questionIndex) => {
    removeQuestionImage(questionIndex);
  };

  const handleRemoveExplanationImage = (questionIndex) => {
    removeExplanationImage(questionIndex);
  };
  
  const handleTestSelect = (testId) => {
    setSelectedTestId(testId);
    setIsViewingTest(true); 
    clearAllImageStates();
    const selectedTest = safeTests.find((test) => (test.id || test._id) === testId);
    if (selectedTest?.questions?.length > 0) {
      selectedTest.questions.forEach((question, index) => {
        if (question.questionImage?.url) setQuestionPreviewUrl(index, question.questionImage.url);
        if (question.explanationImage?.url) setExplanationPreviewUrl(index, question.explanationImage.url);
      });
    }
  };

  const getSelectedTestQuestions = () => {
    if (!selectedTestId) return [];
    const selectedTest = safeTests.find((test) => (test.id || test._id) === selectedTestId);
    return selectedTest?.questions || [];
  };

  const handleBackToTests = () => {
    setIsViewingTest(false); 
    setSelectedTestId("");
  };

  const handleCreateNewClick = () => {
    if (!selectedHospitalId || !selectedTopicId) {
      alert("בחר בית חולים ונושא לפני יצירת מבחן חדש");
      return;
    }
    setShowNameEntryScreen(true);
    setNewTestName("");
  };

  const handleTestNameSubmit = () => {
    if (!newTestName.trim()) {
      alert("שם המבחן לא יכול להיות ריק");
      return;
    }
    setShowNameEntryScreen(false);
    setIsCreatingNew(true); 
    setEditingTestId(null);
    clearAllImageStates();
    setSelectedTestId("");
  };

  const handleCancelNameEntry = () => {
    setShowNameEntryScreen(false);
    setNewTestName("");
  };

  const handleCreateNewTopic = () => {
    setIsCreatingNew(true); 
    setEditingTestId(null);
    setSelectedTopicId("");
    setSelectedTopicName("");
    setSelectedTestId("");
    setNewTestName(""); 
    setShowNameEntryScreen(false);
    clearAllImageStates();
    setIsViewingTest(false);
  };
  
  const handleRenameTopic = (topicId, e) => {
    if (e) e.stopPropagation();
    const topicToEdit = topics.find(topic => (topic.id || topic._id) === topicId);
    if (!topicToEdit) {
      alert("נושא לא נמצא");
      return;
    }
    setTopicToRename(topicId);
    setNewTopicNameInput(topicToEdit.name);
    setIsRenamingTopic(true);
  };

  const handleSaveTopicRename = async () => {
    if (!newTopicNameInput.trim()) {
      alert("שם הנושא לא יכול להיות ריק");
      return;
    }
    try {
      setIsLoading(true);
      const response = await API.put(
        `/api/trainings/training-topics/${encodeURIComponent(topicToRename)}`,
        { name: newTopicNameInput.trim() }
      );
      if (refreshTopics) refreshTopics(); 
      if (selectedTopicId === topicToRename) setSelectedTopicName(newTopicNameInput.trim());
      setIsRenamingTopic(false);
      setTopicToRename(null);
      setNewTopicNameInput("");
      alert(response.data.hebMessage || "שם הנושא עודכן בהצלחה");
    } catch (error) {
      console.error("Error renaming topic:", error);
      alert(`שגיאה בעדכון שם הנושא: ${error.response?.data?.message || error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelTopicRename = () => {
    setIsRenamingTopic(false);
    setTopicToRename(null);
    setNewTopicNameInput("");
  };

const renderTopicRenameModal = () => {
  if (!isRenamingTopic) return null;
  return (
    <div className="training-modal-backdrop">
      <div className="training-modal">
        <div className="training-modal-header"><h3>שינוי שם נושא</h3></div>
        <div className="training-modal-body">
          <div className="training-field-label">שם חדש לנושא</div>
          <input type="text" className="training-text-input" placeholder="הכנס שם נושא חדש"
            value={newTopicNameInput} onChange={(e) => setNewTopicNameInput(e.target.value)} autoFocus />
        </div>
        <div className="training-modal-footer">
          <button className="training-button training-button-secondary" onClick={handleCancelTopicRename}>ביטול</button>
          <button className="training-button training-button-primary" onClick={handleSaveTopicRename} disabled={!newTopicNameInput.trim()}>שמור</button>
        </div>
      </div>
    </div>
  );
};
  
  const handleDeleteTopic = async (topicId, e) => {
    if (e) e.stopPropagation();
    if (!selectedHospitalId || !topicId) {
      alert("אין בית חולים או נושא נבחר"); return;
    }
    if (!window.confirm("האם אתה בטוח שברצונך למחוק את הנושא הזה? כל המבחנים שבו ימחקו גם כן.")) return;

    try {
      setIsLoading(true);
      await API.delete(`/api/trainings/training-topics/${encodeURIComponent(topicId)}`);
      if (selectedTopicId === topicId) { 
        setSelectedTopicId(""); setSelectedTopicName(""); setSelectedTestId("");
        setIsViewingTest(false); setShowTests(false); setTests([]);
      }
      if (refreshTopics) refreshTopics(); 
    } catch (error) {
      console.error("Error deleting topic:", error);
      alert(`שגיאה במחיקת הנושא: ${error.response?.data?.message || error.message}`);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleEditTest = (testId) => {
    setEditingTestId(testId);
    setIsCreatingNew(true); 
    setSelectedTestId(""); 
    clearAllImageStates();
  };

  const handleHospitalSelect = (hospitalId) => {
    setSelectedHospitalId(hospitalId);
    setSelectedTopicId(""); setSelectedTopicName(""); setSelectedTestId("");
    setTests([]); setShowTests(false); clearAllImageStates();
    if (isDragMode) { setIsDragMode(false); stableIdMapRef.current = {}; }
  };

  const handleTopicSelect = (topicId) => {
    if (!selectedHospitalId) { alert("Please select a hospital first"); return; }
    if (!topicId) return;
    if (isDragMode) { setIsDragMode(false); stableIdMapRef.current = {}; }
    
    setSelectedTopicId(topicId);
    const selectedTopicObj = topics.find((topic) => (topic.id || topic._id) === topicId);
    setSelectedTopicName(selectedTopicObj ? selectedTopicObj.name : "");
    setSelectedTestId(""); setIsViewingTest(false); setShowTests(false); 
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "15.4.23"; 
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear().toString().slice(2); 
    return `${day}.${month}.${year}`;
  };

  const getSortedTests = () => {
    if (!Array.isArray(safeTests)) return [];
    return safeTests.slice().sort((a, b) => (a.order || 0) - (b.order || 0));
  };

  const handleDeleteTest = async (testId, e) => {
    if (e) e.stopPropagation();
    if (!selectedTopicId || !selectedHospitalId) { alert("No topic or hospital selected"); return; }    

    try {
      await API.delete(`/api/trainings/training-topic/${encodeURIComponent(selectedTopicId)}/hospital/${encodeURIComponent(selectedHospitalId)}/test/${encodeURIComponent(testId)}`);
      setTests((prevTests) => Array.isArray(prevTests) ? prevTests.filter((test) => (test.id || test._id) !== testId) : []);
      if (selectedTestId === testId) { setSelectedTestId(""); clearAllImageStates(); }
    } catch (error) {
      alert(`שגיאה במחיקת המבחן: ${error.response?.data?.message || error.message}`);
      refreshTestsData(); 
    }
  };

  const handleCancelCreation = (hospitalIdFromChild, topicIdFromChild) => {
    const parentStateBefore = { sHId: selectedHospitalId, sTId: selectedTopicId };

    const finalHospitalId = hospitalIdFromChild || selectedHospitalId || "";
    const finalTopicId = topicIdFromChild === null && !parentStateBefore.sTId 
                         ? ""
                         : (topicIdFromChild || parentStateBefore.sTId || "");
    
    setIsCreatingNew(false); 
    setEditingTestId(null);
    
    setSelectedHospitalId(finalHospitalId); 
    setSelectedTopicId(finalTopicId);     

    if (finalTopicId && finalHospitalId) {
      const topicObj = topics.find(t => (t.id || t._id) === finalTopicId);
      setSelectedTopicName(topicObj ? topicObj.name : ""); 
      
      setShowTests(true);      
      setIsViewingTest(false); 
    } else { 
      setSelectedTopicName("");
      setShowTests(false);
      setIsViewingTest(false);
    }
  };

  const renderTestsGrid = () => {
    if (testsLoading || isLoading) return <div className="training-loading">טוען מבחנים...</div>;
    if (testsError) return <div className="training-error">שגיאה בטעינת מבחנים: {testsError}</div>;
    
    const sortedTests = getSortedTests();
    if (!Array.isArray(safeTests) || !sortedTests || sortedTests.length === 0) {
      return (
        <div className="training-no-tests">
          <div className="training-empty-state">
            <div className="training-empty-icon"><FileText size={36} /></div>
            <h3>אין מבחנים זמינים עבור נושא זה</h3>
            <p>ניתן ליצור מבחן חדש בלחיצה על כפתור ה-+ בתחתית המסך, או ללחוץ "הבא" אם בחרת נושא חדש.</p>
            <button className="training-button training-button-secondary" style={{ marginTop: '1rem' }} onClick={refreshTestsData}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ transform: 'rotate(90deg)' }}><path d="M2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><path d="M12 8V16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><path d="M16 12H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                רענן נתונים
              </div>
            </button>
          </div>
        </div>
      );
    }
    
    return (
      <div className="training-tests-container">
        {sortedTests.length > 1 && !isDragMode && ( 
          <div className="training-drag-controls">
            <button
              className={`training-button training-button-secondary`}
              onClick={toggleDragMode} disabled={isSavingOrder}>
              <GripVertical size={16} />
              <span>שנה סדר מבחנים</span>
            </button>
          </div>
        )}
        {isDragMode && ( 
             <div className="training-drag-controls">
                <button
                className={`training-button training-button-primary`}
                onClick={toggleDragMode} disabled={isSavingOrder}>
                <GripVertical size={16} />
                <span>סיים שינוי סדר</span>
                </button>
                {isSavingOrder && <div className="training-saving-indicator">שומר שינויים...</div>}
            </div>
        )}
        
        {isDragMode ? (
          <DragDropContext onDragEnd={handleDragEnd}>
            <StrictModeDroppable droppableId="testsList">
              {(provided) => (
                <div className="training-tests-grid training-tests-grid-drag-mode" {...provided.droppableProps} ref={provided.innerRef}>
                  {sortedTests.map((test, index) => {
                    const testId = test.id || test._id;
                    const draggableId = getStableId(testId);
                    return (
                      <Draggable key={draggableId} draggableId={draggableId} index={index}>
                        {(providedDraggable, snapshot) => (
                          <div ref={providedDraggable.innerRef} {...providedDraggable.draggableProps}
                            style={{...providedDraggable.draggableProps.style}}
                            className={`training-test-card-wrapper ${snapshot.isDragging ? 'is-dragging' : ''}`}>
                            <div className="training-test-drag-handle" {...providedDraggable.dragHandleProps}><GripVertical size={20} /></div>
                            <TestCard test={test} onSelect={() => {}} onDelete={handleDeleteTest} onEdit={handleEditTest} isDragMode={true} />
                          </div>
                        )}
                      </Draggable>
                    );
                  })}
                  {provided.placeholder}
                </div>
              )}
            </StrictModeDroppable>
          </DragDropContext>
        ) : (
          <div className="training-tests-grid">
            {sortedTests.map((test, index) => (
              <TestCard key={test.id || test._id || `test-${index}`} test={test} onSelect={handleTestSelect} onDelete={handleDeleteTest} onEdit={handleEditTest} />
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderTestQuestions = () => {
    const questions = getSelectedTestQuestions();
    const currentSelectedTest = safeTests.find(test => (test.id || test._id) === selectedTestId); 
    if (!currentSelectedTest) return null;
    
    return (
      <div className="training-test-view">
        <div className="training-test-view-header">
          <div className="training-test-view-header-left">
            <div className="training-test-view-actions">
              <button className="training-button training-button-secondary" onClick={handleBackToTests}>
                <ArrowRight size={16} /><span>חזרה לרשימת המבחנים</span>
              </button>
            </div>
          </div>
          <h2 className="training-test-view-title">{currentSelectedTest.title || `מבחן ${(currentSelectedTest.order || 0) + 1}`}</h2>
          <div className="training-test-view-actions">
            <button className="training-button training-button-secondary" onClick={() => handleEditTest(currentSelectedTest.id || currentSelectedTest._id)}>
              <Edit size={16} /><span>ערוך מבחן</span>
            </button>
            <a href={getClientViewUrl(selectedHospitalId, selectedTopicId, selectedTestId)} target="_blank" rel="noopener noreferrer" className="training-button training-button-primary">
              <ExternalLink size={16} /><span>צפה במבחן</span>
            </a>
          </div>
        </div>
        <div className="questions-list">
          {questions.map((question, index) => (
            <QuestionForm key={`${selectedTestId}-question-${index}`} question={question} index={index}
              onQuestionChange={() => {}} onDeleteQuestion={() => {}} onAddAnswer={() => {}} onRemoveAnswer={() => {}}
              imageData={{
                questionImagePreview: questionImagePreviews[index],
                explanationImagePreview: explanationImagePreviews[index],
                onQuestionImageChange: handleQuestionImageChange,
                onExplanationImageChange: handleExplanationImageChange,
                onRemoveQuestionImage: handleRemoveQuestionImage,
                onRemoveExplanationImage: handleRemoveExplanationImage
              }} readOnly={true} />
          ))}
        </div>
        <FloatingButton icon={ChevronUp} color="16, 60, 110" ariaLabel="Scroll to top of page" className="high-priority" scrollToTop={true} />
      </div>
    );
  };

  const renderNameEntryScreen = () => {
    const currentSelectedHospital = hospitals.find(h => h._id === selectedHospitalId); 
    const currentSelectedTopicObj = topics.find(t => (t.id || t._id) === selectedTopicId); 
    return (
      <div className="training-form-container">
        <div className="training-header-main"><h2 className="training-header-title">יצירת מבחן חדש</h2></div>
        <div className="training-selection-summary">
          בית חולים: {currentSelectedHospital ? currentSelectedHospital.nameHebrew : ''} | 
          נושא המבחן: {currentSelectedTopicObj ? currentSelectedTopicObj.name : ''}
        </div>
        <div className="training-name-entry-section">
          <div className="training-field-label">שם המבחן</div>
          <input type="text" className="training-text-input" placeholder="הכנס שם למבחן"
            value={newTestName} onChange={(e) => setNewTestName(e.target.value)} autoFocus />
          <div className="training-buttons-container">
            <button className="training-button training-button-secondary" onClick={handleCancelNameEntry}>ביטול</button>
            <button className="training-button training-button-primary" onClick={handleTestNameSubmit} disabled={!newTestName.trim()}>
              המשך לעריכת המבחן <ArrowLeft size={16} />
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderWelcomeState = () => {
    if (hospitalsLoading && !selectedHospitalId) { 
        return (
            <div className="training-loading-container">
            <div className="training-loading-spinner"></div>
            <p>טוען נתונים...</p>
            </div>
        );
    }
    return (
      <div className="training-welcome-container">
        <div className="training-welcome-content">
          <div className="training-welcome-icon"><Book size={36} /></div>
          <h2 className="training-welcome-title">ברוכים הבאים לניהול שאלות אימון</h2>
          <p className="training-welcome-text">נהל את כל תוכן הלמידה במקום אחד - צור, ערוך וארגן שאלות בקלות.<br />בחר בית חולים ונושא כדי לצפות במבחנים קיימים, או ליצור תוכן חדש.</p>
          { selectedHospitalId && !selectedTopicId && topicsLoading &&
            <div className="training-loading-container small"><div className="training-loading-spinner"></div><p>טוען נושאים...</p></div>
          }
          { !selectedHospitalId && hospitals.length > 1 &&
             <div className="training-welcome-hint"><Building size={16} /><span>אנא בחר בית חולים להתחלה.</span></div>
          }
           { !selectedHospitalId && hospitals.length === 0 && !hospitalsLoading &&
             <div className="training-welcome-hint"><Building size={16} /><span>לא נמצאו בתי חולים עם הרשאות עריכה.</span></div>
          }
          { selectedHospitalId && topics.length === 0 && !topicsLoading &&
             <div className="training-welcome-hint"><Book size={16} /><span>לא נמצאו נושאים עבור בית חולים זה. תוכל ליצור נושא חדש מהבורר למעלה.</span></div>
          }
          { selectedHospitalId && topics.length > 0 && !selectedTopicId &&
             <div className="training-welcome-hint"><Book size={16} /><span>אנא בחר נושא.</span></div>
          }
        </div>
      </div>
    );
  };

  const renderSelectors = () => (
    <div className="training-selectors-container">
      {hospitals.length > 1 && ( 
        <CustomSelect label="בחירת בית חולים" value={selectedHospitalId} onSelect={handleHospitalSelect}
          options={hospitals.map(h => ({ id: h._id, label: h.nameHebrew, icon: Building }))} 
          placeholder={hospitals.length > 1 ? "בחר בית חולים" : (hospitals[0]?.nameHebrew || "טוען...")} 
          disabled={hospitalsLoading || hospitals.length <=1} 
          />
      )}
      {selectedHospitalId && ( 
        <CustomSelect label="בחירת נושא" value={selectedTopicId} onSelect={handleTopicSelect}
          options={topics.map(t => ({ id: t.id || t._id, label: t.name, icon: Book }))} placeholder="בחר נושא"
          disabled={topicsLoading} showCreateOption={true} createOptionText="צור נושא חדש" onCreateOption={handleCreateNewTopic}
          showDelete={true} onDelete={handleDeleteTopic} showEdit={true} onEdit={handleRenameTopic} />
      )}
    </div>
  );

  return (
    <div className="training-page">
      <div className="training-container">
        {isCreatingNew ? (
          <TrainingCreation 
            onCancel={handleCancelCreation}
            onSuccess={(newData) => {
              const wasEditing = !!editingTestId;
              setIsCreatingNew(false); 
              setEditingTestId(null);

              if (newData && newData.topicName && newData.topicId) { 
                setSelectedHospitalId(selectedHospitalId); 
                setSelectedTopicId(newData.topicId);
                setSelectedTopicName(newData.topicName);
                if (refreshTopics) refreshTopics(); 
                
                setTests([newData]); 
                setSelectedTestId(newData.id || newData._id);
                setIsViewingTest(true); 
                setShowTests(true);      
              } else if (newData) { 
                if (wasEditing) {
                  setTests(prev => Array.isArray(prev) ? prev.map(t => ((t.id || t._id) === (newData.id || newData._id)) ? newData : t) : [newData]);
                } else { 
                  setTests(prev => Array.isArray(prev) ? [...prev.filter(t => (t.id || t._id) !== (newData.id || newData._id)), newData] : [newData]);
                }
                setSelectedTestId(newData.id || newData._id);
                setIsViewingTest(true); 
                setShowTests(true);
              }
            }}
            editingTestId={editingTestId}
            selectedHospitalId={selectedHospitalId}
            selectedTopicId={selectedTopicId}
            initialTestName={newTestName}
            isNewTopic={!editingTestId && !selectedTopicId} 
          />
        ) : showNameEntryScreen ? (
          renderNameEntryScreen()
        ) : (
          <>
            <div className="training-header-main"><h1 className="training-header-title">ניהול שאלות אימון</h1></div>
            {renderSelectors()}
            {selectedTopicId && !isViewingTest && !showTests && ( 
              <div className="training-next-button-container">
                <button className="training-button training-button-primary" onClick={handleShowTests}>הבא <ArrowLeft size={16}/></button>
              </div>
            )}  
            {selectedTopicId && !isViewingTest && showTests && renderTestsGrid()} 
            {selectedTopicId && isViewingTest && renderTestQuestions()}
            {(!selectedHospitalId || (!selectedTopicId && selectedHospitalId)) && !isViewingTest && renderWelcomeState()}
          </>
        )}
      </div>
      {!isCreatingNew && !showNameEntryScreen && !isViewingTest && selectedHospitalId && selectedTopicId && showTests && !isDragMode && (
        <FloatingButton onClick={handleCreateNewClick} icon={Plus} color="31, 181, 163" ariaLabel="Create new test" />
      )}
      {isRenamingTopic && renderTopicRenameModal()}
    </div>
  );
};

export default Training;